import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { useData } from '../../context/DataContext';
import Lightbox from '../common/Lightbox';
import YouTubeEmbed from '../common/YouTubeEmbed';

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { getProject, projects, siteSettings, isLoading } = useData();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const project = getProject(id);

  const projectIndex = projects.findIndex((p) => p.id === id);
  const prevProject = projectIndex > 0 ? projects[projectIndex - 1] : null;
  const nextProject = projectIndex < projects.length - 1 ? projects[projectIndex + 1] : null;

  const whatsappUrl = `https://wa.me/${siteSettings?.whatsappNumber || '905546500984'}?text=${encodeURIComponent(`Merhaba Taşdemir İnşaat, "${project?.title}" projesi hakkında bilgi almak istiyorum.`)}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h2 className="text-2xl text-white mb-4">Proje bulunamadı</h2>
        <Link to="/projeler" className="text-gold hover:text-gold-light">
          Projelere Dön
        </Link>
      </div>
    );
  }

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // İkinci video var mı kontrol et
  const hasSecondVideo = project.videoUrl && project.videoUrl2;

  return (
    <div className="min-h-screen bg-anthracite">
      {/* Hero Image */}
      <section className="relative h-[60vh] md:h-[70vh]">
        <img
          src={project.coverImage}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-anthracite via-anthracite/50 to-transparent" />
        
        {/* Back Button */}
        <Link
          to="/projeler"
          className="absolute top-8 left-8 flex items-center space-x-2 text-white/80 hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Projelere Dön</span>
        </Link>

        {/* Project Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <span
              className={`inline-block px-3 py-1 text-xs font-semibold rounded mb-4 ${
                project.status === 'Biten'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-gold/20 text-gold border border-gold/30'
              }`}
            >
              {project.status}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              {project.title}
            </h1>
            <div className="flex items-center space-x-2 text-gray-300">
              <MapPin className="w-4 h-4 text-gold" />
              <span>{project.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Video Section - Tek veya Çift Video */}
              {project.videoUrl && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-6">Proje Videoları</h2>
                  
                  {hasSecondVideo ? (
                    // İki video yan yana
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <YouTubeEmbed 
                          url={project.videoUrl} 
                          title={project.title}
                          label="Proje Tanıtımı"
                          isShorts={project.videoUrl.includes('/shorts/')}
                        />
                        <p className="text-gray-400 text-sm mt-2 text-center">Proje Tanıtımı</p>
                      </div>
                      <div>
                        <YouTubeEmbed 
                          url={project.videoUrl2} 
                          title={`${project.title} - ${project.videoLabel2 || 'Video 2'}`}
                          label={project.videoLabel2 || 'Video'}
                          isShorts={project.videoUrl2.includes('/shorts/')}
                        />
                        <p className="text-gray-400 text-sm mt-2 text-center">{project.videoLabel2 || 'Video 2'}</p>
                      </div>
                    </div>
                  ) : (
                    // Tek video
                    <YouTubeEmbed 
                      url={project.videoUrl} 
                      title={project.title}
                      isShorts={project.videoUrl.includes('/shorts/')}
                    />
                  )}
                </div>
              )}

              {/* Description */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6">Proje Hakkında</h2>
                <div className="text-gray-400 leading-relaxed whitespace-pre-line">
                  {project.longDescription}
                </div>
              </div>

              {/* Gallery */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Proje Galerisi</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {project.gallery?.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => openLightbox(index)}
                      className="aspect-[4/3] overflow-hidden rounded-lg group"
                    >
                      <img
                        src={img}
                        alt={`${project.title} - Görsel ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                {/* Info Card */}
                <div className="bg-anthracite-light rounded-lg border border-gold/10 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Proje Bilgileri</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-gold/10">
                      <span className="text-gray-400">Durum</span>
                      <span className={`font-medium ${project.status === 'Biten' ? 'text-green-400' : 'text-gold'}`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gold/10">
                      <span className="text-gray-400">Konum</span>
                      <span className="text-white font-medium">{project.location}</span>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="mt-6">
                    <span className="text-gray-400 text-sm">Etiketler</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {project.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-xs text-gold bg-gold/10 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-3 w-full px-6 py-4 bg-gold text-anthracite font-semibold rounded hover:bg-gold-light transition-all duration-300 hover:shadow-lg hover:shadow-gold/20"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Bu Proje Hakkında Bilgi Al</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 border-t border-gold/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {prevProject ? (
              <Link
                to={`/projeler/${prevProject.id}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-gold transition-colors duration-300 group"
              >
                <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
                <div>
                  <span className="text-xs text-gray-500">Önceki Proje</span>
                  <p className="font-medium">{prevProject.title}</p>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {nextProject ? (
              <Link
                to={`/projeler/${nextProject.id}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-gold transition-colors duration-300 group text-right"
              >
                <div>
                  <span className="text-xs text-gray-500">Sonraki Proje</span>
                  <p className="font-medium">{nextProject.title}</p>
                </div>
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && project.gallery && (
        <Lightbox
          images={project.gallery}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetailPage;
